import { Globe } from 'lucide-react'
import { useState } from 'react'
import { updateUser } from '@/client/sdk.gen'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { useAuth } from '@/context'
import { useTranslation } from '@/hooks/useTranslation'
import { SUPPORTED_UI_LANGUAGES } from '@/i18n'

export const SUPPORTED_ANALYSIS_LANGUAGES = [
	'en',
	'nl',
	'pt',
	'tr',
	'fr',
	'de',
	'hy',
	'it',
] as const

export type AnalysisLanguage = (typeof SUPPORTED_ANALYSIS_LANGUAGES)[number]

interface Language {
	code: string
	name: string
	nativeName: string
	flag: string
}

const languages: Language[] = [
	{ code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
	{ code: 'nl', name: 'Dutch', nativeName: 'Nederlands', flag: '🇳🇱' },
	{ code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇵🇹' },
	{ code: 'tr', name: 'Turkish', nativeName: 'Türkçe', flag: '🇹🇷' },
	{ code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
	{ code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
	{ code: 'hy', name: 'Armenian', nativeName: 'Հայերեն', flag: '🇦🇲' },
	{ code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹' },
]

interface LanguageSwitcherProps {
	className?: string
	variant?: 'dropdown' | 'button'
	// Controlled mode props (for analysis language selection)
	value?: AnalysisLanguage
	onChange?: (language: AnalysisLanguage) => void
	mode?: 'ui-language' | 'analysis-language'
}

export default function LanguageSwitcher({
	className = '',
	variant = 'dropdown',
	value,
	onChange,
	mode = 'ui-language',
}: LanguageSwitcherProps) {
	const { user } = useAuth()
	const { currentLanguage, changeLanguage, t } = useTranslation()
	const [isOpen, setIsOpen] = useState(false)

	// Determine if we're in controlled mode (analysis language selection)
	const isControlled = value !== undefined && onChange !== undefined
	const displayLanguage = isControlled ? value : currentLanguage
	const currentLang = languages.find((lang) => lang.code === displayLanguage) || languages[0]

	// Filter languages based on mode
	const availableLanguages =
		mode === 'analysis-language'
			? languages.filter((lang) =>
					(SUPPORTED_ANALYSIS_LANGUAGES as readonly string[]).includes(lang.code)
				)
			: languages.filter((lang) => (SUPPORTED_UI_LANGUAGES as readonly string[]).includes(lang.code))

	// In analysis mode, show names in interface language (primary); native name as secondary
	const getPrimaryLabel = (lang: Language) =>
		mode === 'analysis-language' ? t(`languages.${lang.code}`) : lang.nativeName
	const getSecondaryLabel = (lang: Language) =>
		mode === 'analysis-language' ? lang.nativeName : t(`languages.${lang.code}`)

	/**
	 * Handles the language change event
	 * @param langCode - The language code to change to
	 */
	const handleLanguageChange = async (langCode: string) => {
		if (isControlled) {
			// Controlled mode: just call onChange callback
			onChange(langCode as AnalysisLanguage)
			setIsOpen(false)
			return
		}

		// Uncontrolled mode: update UI language and persist to profile
		// Immediately update frontend language (i18next + localStorage)
		await changeLanguage(langCode)
		setIsOpen(false)

		// Persist language preference via Hey API when user is available
		if (!user?.id) return

		try {
			await updateUser({
				path: {
					user_id: user.id,
				},
				body: {
					lang: langCode,
				},
			})
		} catch (error) {
			// Best-effort persistence only; keep UI language change even if this fails
			console.error('Failed to persist language preference via Hey API', error)
		}
	}

	if (variant === 'button') {
		return (
			<button
				type="button"
				onClick={() => setIsOpen(!isOpen)}
				className={`flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-200 transition-colors ${className}`}
				aria-label={t('language.changeAria')}
			>
				<Globe className="h-4 w-4" />

				<span className="hidden sm:inline">
					{currentLang.flag} {currentLang.nativeName}
				</span>

				<span className="sm:hidden">{currentLang.flag}</span>
			</button>
		)
	}

	return (
		<div className={className}>
			<Select value={displayLanguage} onValueChange={handleLanguageChange}>
				<SelectTrigger
					size="default"
					aria-label={isControlled ? t('language.selectOutputLanguageAria') : t('language.changeAria')}
				>
					<SelectValue
						placeholder={
							<span className="flex items-center gap-2">
								<Globe className="h-4 w-4" />

								<span className="hidden sm:inline">
									{currentLang.flag} {getPrimaryLabel(currentLang)}
								</span>

								<span className="sm:hidden">{currentLang.flag}</span>
							</span>
						}
					/>
				</SelectTrigger>

				<SelectContent className="max-h-[17.5rem]" position="popper">
					{availableLanguages.map((language) => (
						<SelectItem key={language.code} value={language.code}>
							<span className="flex items-center gap-3">
								<span className="text-lg">{language.flag}</span>

								<span className="flex flex-col items-start">
									<span className="font-medium">{getPrimaryLabel(language)}</span>

									<span className="text-xs text-muted-foreground">{getSecondaryLabel(language)}</span>
								</span>
							</span>
						</SelectItem>
					))}
				</SelectContent>
			</Select>
		</div>
	)
}
